import type {
  BuyerLeadListParams,
  BuyerLeadSource,
  BuyerLeadStatus,
  BuyerSubmissionStatus,
  QualificationVerdict,
} from "@/api/buyers";
import type {
  ListFilterDef,
  ListFilterDefs,
  ListFilters,
  ListFilterValues,
} from "@/hooks/useListFilters";
import { US_STATES } from "@/lib/usStates";

// Filter descriptors for the four Buyer CRM list pages. Every buyer-specific
// noun lives HERE; useListFilters and ListFilterBar carry none of it.
//
// One file for all four so the shared vocabularies — status, source, state,
// scope — are declared once. Four descriptor files would have four copies of
// the source list, and the copy that drifts is the one nobody is looking at.

// ---------------------------------------------------------------------------
// Saved views (D5)
// ---------------------------------------------------------------------------

/**
 * A named preset a list page opens on — "Needs action", "New this week".
 *
 * <p>A view is a set of REQUEST PARAMS layered under the URL filters, not a
 * URL filter of its own: the page resolves `{...view.params, ...explicit
 * filters}` so a chip the reader sets on top of a view still wins, and a
 * shared link with `?view=needs-action` opens the same rows for the next
 * reader. `P` is the page's own list-params type, so a preset cannot name a
 * param the server does not accept.</p>
 *
 * <p>`sort` is the sort TOKEN the view implies (a `ListSortOption.value`), and
 * it is a default, not a lock: an explicit choice on the sort control applies
 * over it. `badge` names the stats field whose count the chip shows.</p>
 */
export interface ListViewPreset<P> {
  key: string;
  label: string;
  params: Partial<P>;
  sort?: string;
  /** A one-line description for a tooltip or an empty state. */
  description?: string;
}

export const BUYER_STATUSES: BuyerLeadStatus[] = [
  "New",
  "Assigned",
  "AttemptingContact",
  "Engaged",
  "Qualified",
  "ActivelySubmitting",
  "ConvertedToBorrower",
  "ClosedPurchased",
  "NotQualified",
  "Nurture",
  "Dead",
];

export const BUYER_SOURCES: BuyerLeadSource[] = [
  "Unknown",
  "PaidSocial",
  "PaidSearch",
  "Organic",
  "Referral",
  "PrequalificationForm",
  "InboundCall",
  "InboundEmail",
  "Event",
  "PurchasedList",
  "BusinessBroker",
  "ManualEntry",
  // The /buy landing page. 🔴 A source missing from THIS list is invisible in
  // the filter dropdown AND rejected by BUYER_SOURCE_TOKENS below, so a
  // ?source= deep link for it silently parses to undefined — neither failure
  // shows up in a typecheck.
  "YouTubeLandingPage",
  "ListingSite",
  "Other",
];

// Funnel order, which the filter dropdown renders in — deliberately NOT the
// TypeScript union's order (the two package members are appended there to keep
// the C# enum's ordinals stable). Mirrors BuyerSubmissionProgress on the server.
export const SUBMISSION_STATUSES: BuyerSubmissionStatus[] = [
  "Shortlisted",
  "BuyerApproved",
  "OutreachSent",
  "BrokerResponded",
  "PackageGenerated",
  "PackageSent",
  "InfoReceived",
  "CallMeeting",
  "OfferLoi",
  "UnderContract",
  "ClosedPurchased",
  "EscalatedToDeal",
  "DeclinedByBuyer",
  "NoResponse",
  "RejectedByBroker",
  "Passed",
  "Dead",
];

export const VERDICTS: QualificationVerdict[] = ["Unknown", "Pass", "Concern", "Fail"];

/**
 * Human labels for the PascalCase wire tokens. Only the ones that do not read
 * as English on their own — the rest fall through to a space-separated split,
 * so a value added server-side renders sensibly before anyone touches this map.
 *
 * 🔴 This map is also where the SPECIALIST'S vocabulary is reconciled with the
 * enum's. `BuyerApproved` and `PackageSent` are the two the business calls
 * something else — "Presented to buyer" and "Submitted" — and this file is the
 * whole change, because all eight render sites go through `buyerLabel()`. The
 * enum members are string-stored, rank-pinned and persisted, so the wire tokens
 * must NEVER be renamed to match: a rename would rewrite the meaning of every
 * row already in the book and break the pinned funnel order. The label is
 * presentation; the token is the record.
 *
 * <p>Exported so `statusVocabularyParity.test.ts` can hold it against the shared
 * fixture. Every `BuyerSubmissionStatus` is listed here EXHAUSTIVELY and that is
 * asserted — a submission status is what a specialist reads on a list row all
 * day, so its wording is a decision rather than something to be left to the word
 * splitter. Two of the entries are deliberate identities (`Shortlisted`, `Dead`),
 * which say "the fallback happens to be right" rather than "nobody looked". The
 * other vocabularies stay fallback-first: they are enumerated in dropdowns where
 * "Nurture" and "Organic" need no help.</p>
 */
export const LABEL_OVERRIDES: Record<string, string> = {
  OfferLoi: "Offer / LOI",
  ConvertedToBorrower: "Converted to borrower",
  ClosedPurchased: "Closed — purchased",
  NotQualified: "Not qualified",
  DeclinedByBuyer: "Declined by buyer",
  RejectedByBroker: "Rejected by broker",
  NoResponse: "No response",
  PrequalificationForm: "Prequalification form",
  PaidSocial: "Paid social",
  PaidSearch: "Paid search",
  InboundCall: "Inbound call",
  InboundEmail: "Inbound email",
  PurchasedList: "Purchased list",
  // The /buy landing page. An override rather than the fallback because the
  // PascalCase splitter renders it "You Tube Landing Page".
  YouTubeLandingPage: "YouTube Landing Page",
  ListingSite: "Listing site",
  AttemptingContact: "Attempting contact",
  ActivelySubmitting: "Actively submitting",
  // The two the specialist names differently from the enum. `BuyerApproved` is
  // the point the listing was PRESENTED to the buyer and they said go; the
  // "approved" wording read as though Phoenix had approved something. And a
  // package that has been sent is, in the specialist's words, SUBMITTED — which
  // is also what the record is called everywhere else in the module.
  BuyerApproved: "Presented to buyer",
  PackageSent: "Submitted",
  // 🔴 An IDENTITY, and deliberately so. It is tempting to relabel this one too
  // — "generated" is the machine's word, and the state a specialist is actually
  // in is "drafted, being reviewed" — but Decision 6 was scoped to exactly the
  // two above, and `AssociatedListingsTab.test.tsx`'s "leaves the neighbouring
  // statuses alone" pins that scope by asserting this renders "Package
  // generated". Relabelling it here is a product decision, not a completeness
  // fix, and it belongs to whoever owns Decision 6.
  //
  // Listed rather than left to the word splitter because the exhaustive pin over
  // BuyerSubmissionStatus needs every member to be a decision somebody made — an
  // identity says "considered, and the plain wording is right", which is a
  // different statement from silence.
  PackageGenerated: "Package generated",
  OutreachSent: "Outreach sent",
  BrokerResponded: "Broker responded",
  InfoReceived: "Info received",
  CallMeeting: "Call / meeting",
  UnderContract: "Under contract",
  EscalatedToDeal: "Escalated to deal",
  // 🔴 The fallback rendered this as a bare "Passed", which reads as a PASS —
  // the opposite of what it is. It is one of the five terminal statuses, so a
  // reader glancing at a list row saw a submission that had died described with
  // the vocabulary of one that had cleared something.
  //
  // "Passed on" fixes the polarity and asserts nothing further. Nothing in the
  // codebase says WHO passed — the enum has no per-member doc, and neither the
  // service nor the DTOs distinguish it from `DeclinedByBuyer` — so naming a
  // subject here would be inventing the fact rather than labelling it.
  Passed: "Passed on",
  // More identities, on the same reasoning. "Dead" is blunt and correct;
  // softening it would blur the line against Nurture, which is the one that
  // means "come back later".
  Shortlisted: "Shortlisted",
  Dead: "Dead",
};

/** PascalCase → readable. Falls back to a word split so an unmapped value that
 *  arrives from a newer server still reads as words rather than as a token. */
export function buyerLabel(token: string): string {
  const override = LABEL_OVERRIDES[token];
  if (override) return override;
  const words = token.replace(/([a-z])([A-Z])/g, "$1 $2");
  return words.charAt(0) + words.slice(1).toLowerCase();
}

const BUYER_STATUS_TOKENS = new Set<string>(BUYER_STATUSES);
const BUYER_SOURCE_TOKENS = new Set<string>(BUYER_SOURCES);
const SUBMISSION_STATUS_TOKENS = new Set<string>(SUBMISSION_STATUSES);
const VERDICT_TOKENS = new Set<string>(VERDICTS);
const STATE_TOKENS = new Set<string>(US_STATES.map((s) => s.code));

/** Identity helper: pins each filter's value type so the hook infers it back
 *  out instead of widening a literal union to `string`. */
function filter<V>(def: ListFilterDef<V>): ListFilterDef<V> {
  return def;
}

/**
 * Whose book a list shows. `mine` is the specialist's default view and `all`
 * the manager's.
 *
 * <p>🔴 <b>The server DOES filter on it and derives no authority from it —
 * two different statements, and the second is the one that matters.</b>
 * `mine` sends `mineOnly: true`, which `BuyerLeadService.ApplyFilters` honours
 * with a real `WHERE AssignedSpecialistUserId = me`; so this is not a
 * client-side lens and must not be reimplemented as one. What it is not is a
 * permission boundary: reads are open to every internal role (L37), so `all`
 * shows a specialist the whole book by design, and nothing about the value
 * widens or narrows what the caller is allowed to see.</p>
 *
 * <p>Team scoping does not enter here either. That governs DISPATCH — the
 * assignment queue, the candidate list, who may be handed a buyer — and
 * `BuyerTeamScope` is read on those paths, never on this one.</p>
 */
export type BuyerScope = "all" | "mine";

/**
 * <p>🔴 <b>`control: "hidden"` because the page renders a `ScopeToggle`, not
 * because the filter is hidden.</b> Standard §9.2 makes mine-vs-everyone a
 * two-button segmented control rather than one more dropdown: the two scopes are
 * exhaustive and mutually exclusive, so it is a lens over the whole set and not a
 * filter that combines with the others — and the question "is this my queue or
 * the whole book" is the first one a reader asks, so its answer belongs at rest
 * rather than behind a click.</p>
 *
 * <p>🔴 <b>The default is UNSET, not "all"</b> (Addendum B, B5). Both values are
 * written to the URL, so a link that says <code>?scope=all</code> or
 * <code>?scope=mine</code> opens on what it says; a URL without one opens on the
 * reader's remembered choice, and on "mine" the first time
 * (<code>useRememberedScope</code> in <code>listScope.ts</code>). Read the
 * effective scope from that hook, never from this value.</p>
 */
const scopeFilter = filter<BuyerScope | "">({
  control: "hidden",
  defaultValue: "",
  label: "Scope filter",
  options: [
    { value: "all", label: "Everyone" },
    { value: "mine", label: "Mine" },
  ],
  parse: (raw) => (raw === "mine" || raw === "all" ? raw : undefined),
});

const searchFilter = (placeholder: string) =>
  filter<string>({
    control: "search",
    defaultValue: "",
    placeholder,
    className: "grow",
    // Without this every keystroke re-keys both the list and the stats query.
    debounceMs: 300,
  });

/**
 * The stage-bar segment currently filtering a list.
 *
 * <p>URL-only, like the card filter — the bar IS the control, so rendering a
 * second one in the filter bar would be two ways to say the same thing.</p>
 */
const phaseFilter = filter<string>({
  control: "hidden",
  defaultValue: "",
  parse: (raw) => raw || undefined,
});

// ---------------------------------------------------------------------------
// Sort
// ---------------------------------------------------------------------------

/**
 * One entry in a list page's sort vocabulary.
 *
 * <p>🔴 <b>SORTING IS SERVER-SIDE AND ALWAYS WAS.</b> <c>PagedQuery</c> carries
 * <c>SortBy</c>/<c>SortDirection</c>, and all three Buyer CRM services have a
 * whitelisted <c>ApplySort</c> — every arm ending in a unique <c>Id</c> tiebreak,
 * so paging is stable (Standard §10). What was missing was any way to REACH it:
 * the client mirrors omit the two fields and no page ever sent them. So this is
 * a capability being surfaced, not one being invented — and it must not be
 * re-implemented client-side, because a client sort can only reorder the 25 rows
 * the server already chose, which is a control that cannot do what it says.</p>
 *
 * <p><b>Nullable columns are why this list is shorter than the whitelist.</b>
 * <c>BuyerLeadService</c> and <c>ListingService</c> sort ascending with a plain
 * <c>OrderBy</c> on a nullable column, so NULL — the smallest value there is —
 * comes FIRST. "Lowest asking price" would therefore open onto every listing
 * with no price stated, which is the defect
 * <c>BuyerSubmissionService.ApplySort</c> already documents and fixed for
 * follow-up dates with a COALESCE. Rather than ship a control that lies, the
 * money and follow-up sorts are offered DESCENDING only (where NULL lands last
 * for free), and the ones that cannot be made honest are omitted entirely. See
 * the notes on each list below.</p>
 */
export interface ListSortOption {
  /** URL token. */
  value: string;
  label: string;
  /**
   * The server's whitelisted key. OMITTED on the default option, so a page at
   * rest sends no sort params at all and issues the byte-identical request it
   * always did — the server's fallback arm IS `createdAt` descending.
   */
  sortBy?: string;
  sortDirection?: "asc" | "desc";
}

/** What the page sends. Resolved once so no page re-derives the mapping. */
export interface ResolvedSort {
  sortBy?: string;
  sortDirection?: "asc" | "desc";
}

export function resolveSort(options: ListSortOption[], value: string): ResolvedSort {
  const option = options.find((o) => o.value === value) ?? options[0];
  return { sortBy: option.sortBy, sortDirection: option.sortDirection };
}

/** The sort descriptor. `hidden` because the page renders `SortSelect` itself —
 *  §3.2 puts the sort control right-aligned beside the search box, which is not
 *  where a descriptor-ordered filter would land. */
const sortFilter = (options: ListSortOption[]) => {
  const tokens = new Set(options.map((o) => o.value));
  return filter<string>({
    control: "hidden",
    defaultValue: options[0].value,
    parse: (raw) => (tokens.has(raw) ? raw : undefined),
  });
};

/**
 * Buyers.
 *
 * <p>OMITTED: <c>nextFollowUpDate</c>. The server sorts it ascending with a bare
 * <c>OrderBy</c> on a nullable column, so "follow-up soonest" would list every
 * buyer with no follow-up at all before the ones that are due. The fix belongs
 * in <c>BuyerLeadService.ApplySort</c> (mirror the submissions service's
 * COALESCE), not in a client that cannot reorder across pages.</p>
 *
 * <p><b><c>lastContactAtUtc</c> ascending is KEPT</b>, and its NULLs-first
 * behaviour is the reason rather than a flaw: a buyer never contacted is the
 * extreme of "least recently contacted", so the top of that list is exactly
 * where they belong.</p>
 */
export const BUYER_SORTS: ListSortOption[] = [
  { value: "newest", label: "Newest first" },
  { value: "oldest", label: "Oldest first", sortBy: "createdAt", sortDirection: "asc" },
  { value: "surname", label: "Surname A–Z", sortBy: "lastName", sortDirection: "asc" },
  // NOT the persisted status string — the server ranks by FUNNEL POSITION
  // (`StatusRankKey`), because `Status` is stored as text and sorting the column
  // sorts by spelling.
  { value: "furthest", label: "Furthest in funnel", sortBy: "status", sortDirection: "desc" },
  {
    value: "capacity",
    label: "Largest capacity",
    sortBy: "maxPurchasePrice",
    sortDirection: "desc",
  },
  {
    value: "quietest",
    label: "Least recently contacted",
    sortBy: "lastContactAtUtc",
    sortDirection: "asc",
  },
  // The server's urgency rank, then the next action's date, then Id — the
  // order the "Needs action" view opens in.
  { value: "needs-action", label: "Needs action first", sortBy: "needsaction", sortDirection: "asc" },
];

/**
 * The buyers list's saved views.
 *
 * <p>`new-this-week` sends `createdAfter`, which the server does not read yet
 * (declared ahead — see `BuyerLeadListParams`); until it does the view is its
 * `newest` sort. `needs-contact` takes its window from the stats payload at
 * render time, so it carries no param here. `all` is the plain list.</p>
 */
export const BUYER_VIEWS: ListViewPreset<BuyerLeadListParams>[] = [
  { key: "needs-action", label: "Needs action", params: { needsActionOnly: true }, sort: "needs-action" },
  { key: "new-this-week", label: "New this week", params: {}, sort: "newest" },
  { key: "needs-contact", label: "Needs contact", params: {} },
  { key: "qualified-idle", label: "Qualified, no submission", params: { qualifiedNoSubmissionOnly: true } },
  { key: "actively-submitting", label: "Actively submitting", params: { status: ["ActivelySubmitting"] } },
  { key: "nurture", label: "Nurture", params: { status: ["Nurture"] } },
  { key: "all", label: "All buyers", params: {} },
];

const BUYER_VIEW_TOKENS = new Set(BUYER_VIEWS.map((v) => v.key));

/** The view the reader lands on with no choice made anywhere: their own book
 *  to work (F2), everyone else's the whole list. */
export function defaultBuyerView(isSpecialist: boolean): string {
  return isSpecialist ? "needs-action" : "all";
}

/**
 * Listings.
 *
 * <p>OMITTED: <c>businessName</c>. Two independent reasons, either sufficient —
 * a blind listing legitimately has no business name, so ascending would stack
 * every anonymous listing at the top; and the column the server sorts is NOT the
 * one the cell renders, which walks the display-name ladder (business name →
 * headline → the URL's host). "Business name A–Z" would order rows by a value
 * the reader cannot see.</p>
 *
 * <p>OMITTED: <c>state</c>. Already a filter on this page, and nullable.</p>
 */
export const LISTING_SORTS: ListSortOption[] = [
  { value: "newest", label: "Newest first" },
  { value: "oldest", label: "Oldest first", sortBy: "createdAt", sortDirection: "asc" },
  {
    value: "asking",
    label: "Highest asking price",
    sortBy: "askingPrice",
    sortDirection: "desc",
  },
  { value: "cashflow", label: "Highest cash flow", sortBy: "cashFlow", sortDirection: "desc" },
];

/**
 * Submissions.
 *
 * <p>The one page where a follow-up sort is honest in BOTH directions:
 * <c>BuyerSubmissionService.ApplySort</c> COALESCEs an absent date to
 * <c>MaxValue</c> on the ascending arm, so "nothing scheduled" sorts last either
 * way. Offer prices are nullable and un-COALESCEd, so that one stays
 * descending.</p>
 */
export const SUBMISSION_SORTS: ListSortOption[] = [
  { value: "newest", label: "Newest first" },
  { value: "oldest", label: "Oldest first", sortBy: "createdAt", sortDirection: "asc" },
  { value: "furthest", label: "Furthest along", sortBy: "status", sortDirection: "desc" },
  {
    value: "follow-up",
    label: "Follow-up soonest",
    sortBy: "nextFollowUpDate",
    sortDirection: "asc",
  },
  {
    // The token stays `offer`: it is in saved views and URLs.
    value: "offer",
    label: "Largest deal structure price",
    sortBy: "dealStructurePrice",
    sortDirection: "desc",
  },
];

// ---------------------------------------------------------------------------
// Progressive chrome (Standard §5)
// ---------------------------------------------------------------------------

/**
 * Has the reader narrowed this list at all?
 *
 * <p>Reads <c>inputs</c> rather than <c>values</c>, so a half-typed search — text
 * that has not reached the URL yet — counts. That is what keeps the search box
 * on screen for the reader who is typing in it, which is the whole point of §5
 * keying its threshold on something other than the rows currently rendered.</p>
 */
export function anyFilterActive<D extends ListFilterDefs>(filters: ListFilters<D>): boolean {
  const inputs = filters.inputs as Record<string, unknown>;
  return Object.keys(filters.defs).some(
    (key) => !Object.is(inputs[key], filters.defs[key].defaultValue),
  );
}

// ---------------------------------------------------------------------------
// Buyers
// ---------------------------------------------------------------------------

export const BUYER_FILTERS = {
  q: searchFilter("Search name, company, email, phone…"),
  status: filter<BuyerLeadStatus | "">({
    control: "select",
    defaultValue: "",
    label: "Status filter",
    className: "w-48",
    options: [
      { value: "", label: "All statuses" },
      ...BUYER_STATUSES.map((s) => ({ value: s, label: buyerLabel(s) })),
    ],
    // An unrecognised token falls back to "all" rather than being forwarded, so
    // a hand-edited or stale URL degrades to the default view.
    parse: (raw) => (BUYER_STATUS_TOKENS.has(raw) ? (raw as BuyerLeadStatus) : undefined),
  }),
  source: filter<BuyerLeadSource | "">({
    control: "select",
    defaultValue: "",
    label: "Source filter",
    className: "w-44",
    options: [
      { value: "", label: "All sources" },
      ...BUYER_SOURCES.map((s) => ({ value: s, label: buyerLabel(s) })),
    ],
    parse: (raw) => (BUYER_SOURCE_TOKENS.has(raw) ? (raw as BuyerLeadSource) : undefined),
  }),
  verdict: filter<QualificationVerdict | "">({
    control: "select",
    defaultValue: "",
    label: "Qualification filter",
    className: "w-40",
    options: [
      { value: "", label: "Any qualification" },
      ...VERDICTS.map((v) => ({ value: v, label: buyerLabel(v) })),
    ],
    parse: (raw) => (VERDICT_TOKENS.has(raw) ? (raw as QualificationVerdict) : undefined),
  }),
  scope: scopeFilter,
  /**
   * The reversible archive, as a THREE-VALUE select rather than a checkbox.
   *
   * <p>"Show archived" as a checkbox would have to mean one of two things and
   * could not say which: everything including archived, or archived only. The
   * first makes the list a mixed set where a badge is the only clue; the second
   * is what someone hunting for a record they archived last week actually
   * wants. Naming all three states is the only way the control tells the truth
   * about which one is active.</p>
   *
   * <p>Default is empty — the working list, which is every existing bookmark
   * and every existing test, unchanged.</p>
   */
  archived: filter<"" | "only">({
    control: "select",
    defaultValue: "",
    label: "Archived filter",
    className: "w-44",
    options: [
      { value: "", label: "Hide archived" },
      { value: "only", label: "Archived only" },
    ],
    parse: (raw) => (raw === "only" ? "only" : undefined),
  }),
  /**
   * The stat-card quick filters. URL-only — the cards are the control, so
   * rendering a second one in the bar would be two ways to say the same thing.
   */
  card: filter<BuyerCardFilter>({
    control: "hidden",
    defaultValue: "",
    parse: (raw) => (CARD_TOKENS.has(raw) ? (raw as BuyerCardFilter) : undefined),
  }),
  /**
   * The stage-bar segment driving the list, if any — a rung of the buyer phase
   * ladder, translated by the page into the statuses it stands for.
   *
   * <p>Deliberately PERMISSIVE here rather than validated against the ladder's
   * keys: <c>buyerPhases</c> imports <c>buyerLabel</c> from this file, so
   * validating here would close an import cycle for no benefit. The page owns
   * the translation and an unrecognised rung yields no statuses, which degrades
   * a stale bookmark to the unfiltered view.</p>
   */
  phase: phaseFilter,
  sort: sortFilter(BUYER_SORTS),
  /**
   * The saved view. URL-only — the toolbar's view chips are the control. Empty
   * means "no choice in the URL", and the page then falls back to the reader's
   * persisted preference and then to their role's default, so a bare `/buyers`
   * keeps opening where they left it.
   */
  view: filter<string>({
    control: "hidden",
    defaultValue: "",
    parse: (raw) => (BUYER_VIEW_TOKENS.has(raw) ? raw : undefined),
  }),
  /** One team's leads (`buyerTeamId`). Rendered as a select only for a
   *  `Buyers.ManageAllTeams` holder; everyone else sees their own book. */
  team: filter<string>({
    control: "hidden",
    defaultValue: "",
    label: "Team filter",
    parse: (raw) => raw || undefined,
  }),
};

/**
 * The filters this page renders as §9.1 chips rather than as a `<select>`.
 *
 * <p>All three are short closed vocabularies whose option set is itself part of
 * the information — the standard names status and verdict explicitly. `scope` is
 * absent because a mine-vs-everyone partition is a `ScopeToggle`, not a chip
 * (§9.2): chips read as additive, and "mine" is a lens over the whole set rather
 * than one filter among several.</p>
 */
export const BUYER_CHIP_KEYS = ["status", "source", "verdict"];

/** Which stat card or attention pill is driving the list, if any. Mutually
 *  exclusive by construction: one param, one value. */
export type BuyerCardFilter =
  | ""
  | "unassigned"
  | "qualified"
  | "submitting"
  | "converted"
  | "overdue"
  | "due-today"
  | "no-contact"
  | "qualified-no-submission";

const CARD_TOKENS = new Set<string>([
  "unassigned",
  "qualified",
  "submitting",
  "converted",
  "overdue",
  "due-today",
  "no-contact",
  "qualified-no-submission",
]);

export type BuyerFilterValues = ListFilterValues<typeof BUYER_FILTERS>;

// ---------------------------------------------------------------------------
// Listings
// ---------------------------------------------------------------------------

export const LISTING_FILTERS = {
  q: searchFilter("Search business, headline, industry, marketplace…"),
  state: filter<string>({
    control: "select",
    defaultValue: "",
    label: "State filter",
    className: "w-44",
    options: [
      { value: "", label: "All states" },
      ...US_STATES.map((s) => ({ value: s.code, label: `${s.name} (${s.code})` })),
    ],
    parse: (raw) => (STATE_TOKENS.has(raw) ? raw : undefined),
  }),
  industry: filter<string>({
    control: "search",
    defaultValue: "",
    placeholder: "Industry",
    className: "w-44",
    debounceMs: 300,
  }),
  marketplace: filter<string>({
    control: "search",
    defaultValue: "",
    placeholder: "Marketplace",
    className: "w-44",
    debounceMs: 300,
  }),
  /** "In play" — listings with at least one live submission. */
  inPlay: filter<boolean>({
    control: "select",
    defaultValue: false,
    label: "Activity filter",
    className: "w-40",
    options: [
      { value: "", label: "All listings" },
      { value: "1", label: "In play" },
    ],
    parse: (raw) => (raw === "1" ? true : raw === "" ? false : undefined),
    serialize: (v) => (v ? "1" : null),
  }),
  /** Which attention pill is driving the list. One param, one value. */
  card: filter<ListingCardFilter>({
    control: "hidden",
    defaultValue: "",
    parse: (raw) =>
      LISTING_CARD_TOKENS.has(raw) ? (raw as ListingCardFilter) : undefined,
  }),
  /** A rung of the listing ENGAGEMENT ladder — derived server-side from the
   *  listing's own submissions, since a listing has no status column. */
  phase: phaseFilter,
  sort: sortFilter(LISTING_SORTS),
};

/**
 * Chips on Listings — `inPlay` only.
 *
 * <p>🔴 <b><c>state</c> stays a `&lt;select&gt;`, deliberately.</b> Standard §9.1
 * replaces a dropdown for a "closed, SHORT vocabulary … where the option set is
 * part of the information", and preserves the select for "a genuinely long,
 * unbounded or free-text-searchable option list". Fifty-one US states is the
 * long case: as chips it would be a wall of pills taller than the table it
 * filters, and nobody reads the option set of a state picker to learn which
 * states exist. `industry` and `marketplace` are free-text search inputs and are
 * not selects at all.</p>
 */
export const LISTING_CHIP_KEYS = ["inPlay"];

export type ListingCardFilter = "" | "no-agent-email" | "no-price" | "no-buyer";

const LISTING_CARD_TOKENS = new Set<string>([
  "no-agent-email",
  "no-price",
  "no-buyer",
]);

export type ListingFilterValues = ListFilterValues<typeof LISTING_FILTERS>;

// ---------------------------------------------------------------------------
// Submissions
// ---------------------------------------------------------------------------

export const SUBMISSION_FILTERS = {
  q: searchFilter("Search buyer or listing…"),
  status: filter<BuyerSubmissionStatus | "">({
    control: "select",
    defaultValue: "",
    label: "Status filter",
    className: "w-48",
    options: [
      { value: "", label: "All statuses" },
      ...SUBMISSION_STATUSES.map((s) => ({ value: s, label: buyerLabel(s) })),
    ],
    parse: (raw) =>
      SUBMISSION_STATUS_TOKENS.has(raw) ? (raw as BuyerSubmissionStatus) : undefined,
  }),
  /**
   * Live-only is a FLAG rather than an enumeration of the nine live statuses:
   * that list grows (PR 2c adds one), and an enumerated filter would silently
   * stop matching the new member.
   */
  liveOnly: filter<boolean>({
    control: "select",
    defaultValue: false,
    label: "Lifecycle filter",
    className: "w-40",
    options: [
      { value: "", label: "All submissions" },
      { value: "1", label: "Live only" },
    ],
    parse: (raw) => (raw === "1" ? true : raw === "" ? false : undefined),
    serialize: (v) => (v ? "1" : null),
  }),
  overdue: filter<boolean>({
    control: "select",
    defaultValue: false,
    label: "Follow-up filter",
    className: "w-44",
    options: [
      { value: "", label: "Any follow-up" },
      { value: "1", label: "Overdue only" },
    ],
    parse: (raw) => (raw === "1" ? true : raw === "" ? false : undefined),
    serialize: (v) => (v ? "1" : null),
  }),
  scope: scopeFilter,
  /** Set by a deep link from a buyer or a listing; no control of its own. */
  buyerLeadId: filter<string | undefined>({
    control: "hidden",
    defaultValue: undefined,
    parse: (raw) => raw || undefined,
  }),
  listingId: filter<string | undefined>({
    control: "hidden",
    defaultValue: undefined,
    parse: (raw) => raw || undefined,
  }),
  /** Which attention pill is driving the list. One param, one value. */
  card: filter<SubmissionCardFilter>({
    control: "hidden",
    defaultValue: "",
    parse: (raw) =>
      SUBMISSION_CARD_TOKENS.has(raw) ? (raw as SubmissionCardFilter) : undefined,
  }),
  /** A rung of the submission phase ladder — the SAME six the detail header's
   *  stepper renders, so the index and the record tell one story. */
  phase: phaseFilter,
  sort: sortFilter(SUBMISSION_SORTS),
};

/**
 * Chips on Submissions.
 *
 * <p>`liveOnly` and `overdue` are the clearest cases in the module: each is a
 * BOOLEAN wearing a two-option `&lt;select&gt;`, so the dropdown hides one of two
 * possible answers behind a click. `scope` is a `ScopeToggle` (§9.2), and
 * `buyerLeadId`/`listingId` are deep-link params with no control at all — the
 * scoped-view banner is their affordance.</p>
 */
export const SUBMISSION_CHIP_KEYS = ["status", "liveOnly", "overdue"];

export type SubmissionCardFilter =
  | ""
  | "awaiting-broker"
  | "package-not-sent"
  | "nda-outstanding"
  | "quiet";

const SUBMISSION_CARD_TOKENS = new Set<string>([
  "awaiting-broker",
  "package-not-sent",
  "nda-outstanding",
  "quiet",
]);

export type SubmissionFilterValues = ListFilterValues<typeof SUBMISSION_FILTERS>;

// ---------------------------------------------------------------------------
// Assignment queue
// ---------------------------------------------------------------------------

export const ASSIGNMENT_QUEUE_FILTERS = {
  q: searchFilter("Search name, company, email, phone…"),
  source: filter<BuyerLeadSource | "">({
    control: "select",
    defaultValue: "",
    label: "Source filter",
    className: "w-44",
    options: [
      { value: "", label: "All sources" },
      ...BUYER_SOURCES.map((s) => ({ value: s, label: buyerLabel(s) })),
    ],
    parse: (raw) => (BUYER_SOURCE_TOKENS.has(raw) ? (raw as BuyerLeadSource) : undefined),
  }),
  staleOnly: filter<boolean>({
    control: "select",
    defaultValue: false,
    label: "Age filter",
    className: "w-44",
    options: [
      { value: "", label: "Whole queue" },
      { value: "1", label: "Waiting too long" },
    ],
    parse: (raw) => (raw === "1" ? true : raw === "" ? false : undefined),
    serialize: (v) => (v ? "1" : null),
  }),

  /**
   * Which team's queue (multi-manager teams, WS3, D6).
   *
   * <p>🔴 <b>`control: "hidden"` because its OPTIONS ARE FETCHED, not because
   * the filter is hidden.</b> Every other descriptor here declares a static
   * option list; the teams are rows in a table, so the page renders the
   * `<select>` from `useBuyerTeams()` and this entry exists purely to give the
   * value its URL round-trip — the same reason `scopeFilter` above is hidden,
   * arrived at from the other direction.</p>
   *
   * <p>🔴 <b>It is CHROME, not the gate.</b> The page only renders the control
   * for a `Buyers.ManageAllTeams` holder, but the server resolves the caller's
   * scope from their own membership and treats this value as a NARROWING within
   * it — a one-team manager who hand-edits the URL gets their own team, and a
   * request for a team they do not dispatch gets an empty queue rather than
   * somebody else's rows. Hiding a control is never the boundary.</p>
   *
   * <p>Parsed loosely: a malformed id is `undefined`, which the hook turns back
   * into the default (no filter) rather than forwarding junk to the server. A
   * bare non-empty string is accepted rather than shape-checked as a GUID —
   * the server 400s or returns nothing for an id that matches no team, and a
   * client-side regex would be a second definition of "a team id".</p>
   */
  buyerTeamId: filter<string>({
    control: "hidden",
    defaultValue: "",
    label: "Team filter",
  }),
};

export type AssignmentQueueFilterValues = ListFilterValues<typeof ASSIGNMENT_QUEUE_FILTERS>;
